import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { FaCheckCircle, FaCalculator, FaFileInvoiceDollar, FaChartPie, FaUserTie, FaGavel, FaClipboardList } from 'react-icons/fa'
import PageHero from '../../components/PageHero/PageHero'

const CryptoTaxServices = () => {
  return (
    <>
      <Helmet>
        <title>Crypto Tax Services - Proof of Reserves Auditor</title>
        <meta name="description" content="Specialized cryptocurrency tax planning, compliance, and advisory services for digital asset companies and high-net-worth individuals." />
      </Helmet>

      <div className="service-page">
        <PageHero
          title="Crypto Tax Services"
          description="Expert tax planning, compliance, and advisory services for cryptocurrency and digital asset businesses"
        >
          <div className="flex flex-wrap gap-6 mb-8 justify-center">
            <div className="flex items-center space-x-2">
              <FaCheckCircle className="text-accent" />
              <span>Crypto-Native CPAs</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaCheckCircle className="text-accent" />
              <span>Multi-Jurisdictional Expertise</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaCheckCircle className="text-accent" />
              <span>DeFi & NFT Specialists</span>
            </div>
          </div>
          <Link to="/contact" className="btn-primary bg-accent hover:bg-accent-dark">
            Schedule Tax Consultation
          </Link>
        </PageHero>

        {/* Overview Section */}
        <section className="section-padding bg-white">
          <div className="container-custom max-w-4xl">
            <h2 className="section-title">Overview</h2>
            <p className="text-lg text-gray-600 mb-6">
              Cryptocurrency taxation is complex and constantly evolving. Our crypto-native tax professionals understand the intricacies of digital asset transactions, from basic trading to complex DeFi protocols and NFT marketplaces. We help businesses and individuals navigate cryptocurrency tax obligations while optimizing their tax position.
            </p>
            <p className="text-lg text-gray-600">
              Whether you're a crypto business, protocol, or high-net-worth individual, our team provides comprehensive tax planning, compliance, and advisory services tailored to the unique challenges of the digital asset ecosystem.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="section-title text-center mb-12">Comprehensive Tax Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaFileInvoiceDollar className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Tax Return Preparation</h3>
                <p className="text-gray-600">
                  Complete preparation of federal and state tax returns including proper reporting of cryptocurrency transactions, gains, and income.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaCalculator className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Capital Gains Calculation</h3>
                <p className="text-gray-600">
                  Accurate calculation of capital gains and losses across all transactions using appropriate cost basis methodologies (FIFO, LIFO, HIFO).
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaChartPie className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Tax Planning & Strategy</h3>
                <p className="text-gray-600">
                  Proactive tax planning to minimize liabilities through harvest strategies, entity structuring, and timing optimization.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaUserTie className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Business Entity Advisory</h3>
                <p className="text-gray-600">
                  Guidance on optimal entity structures for crypto businesses including C-Corp, S-Corp, LLC, and international entity considerations.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaGavel className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">IRS Audit Defense</h3>
                <p className="text-gray-600">
                  Expert representation during IRS examinations, audits, and disputes related to cryptocurrency transactions and reporting.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <FaClipboardList className="text-4xl text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3 text-primary">Transaction Documentation</h3>
                <p className="text-gray-600">
                  Comprehensive record-keeping systems and documentation to support tax positions and withstand IRS scrutiny.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Specialized Areas */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <h2 className="section-title text-center mb-12">Specialized Tax Advisory</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-3 text-primary">DeFi Protocol Taxation</h3>
                <p className="text-gray-600 mb-4">
                  Navigate the complex tax treatment of DeFi activities including:
                </p>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Yield farming and liquidity mining rewards</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Staking rewards and governance token distributions</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Impermanent loss recognition and optimization</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Flash loan and arbitrage transaction reporting</span>
                  </li>
                </ul>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-3 text-primary">NFT Tax Treatment</h3>
                <p className="text-gray-600 mb-4">
                  Specialized guidance for NFT creators, traders, and marketplaces:
                </p>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Collectible vs. capital asset classification</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Creator royalty income reporting</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>NFT valuation methodologies</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Marketplace transaction fee deductions</span>
                  </li>
                </ul>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-3 text-primary">Mining & Staking Operations</h3>
                <p className="text-gray-600 mb-4">
                  Tax optimization for mining and staking businesses:
                </p>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Business vs. hobby classification</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Equipment depreciation strategies</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Energy cost deductions and credits</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Self-employment tax optimization</span>
                  </li>
                </ul>
              </div>

              <div className="border-l-4 border-accent pl-6">
                <h3 className="text-xl font-bold mb-3 text-primary">International Tax Compliance</h3>
                <p className="text-gray-600 mb-4">
                  Navigate cross-border crypto tax obligations:
                </p>
                <ul className="text-gray-600 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>FBAR and FATCA reporting requirements</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Foreign exchange gain/loss calculations</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Treaty benefits and international structuring</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>Transfer pricing for multi-national operations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Business Services */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom max-w-4xl">
            <h2 className="section-title">Tax Services for Crypto Businesses</h2>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Exchange & Trading Platform Tax Compliance</h3>
                <p className="text-gray-600">
                  Comprehensive tax compliance for exchanges including Form 1099 preparation, information reporting, backup withholding, and state sales tax nexus analysis.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Protocol & DAO Tax Structuring</h3>
                <p className="text-gray-600">
                  Entity selection and tax structuring for DeFi protocols and DAOs, including foundation setup, treasury management, and contributor compensation planning.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Token Launch Tax Planning</h3>
                <p className="text-gray-600">
                  Pre-launch tax planning for token issuances including SAFTs, airdrops, and public sales with consideration of securities law implications.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">Payroll & Contractor Management</h3>
                <p className="text-gray-600">
                  Crypto-denominated compensation planning including W-2 vs. 1099 classification, withholding calculations, and equity compensation strategies.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-lg mb-2 text-primary">R&D Tax Credits</h3>
                <p className="text-gray-600">
                  Identification and documentation of qualified research activities to maximize federal and state R&D tax credits for blockchain development.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tax Planning Strategies */}
        <section className="section-padding bg-white">
          <div className="container-custom max-w-4xl">
            <h2 className="section-title">Proactive Tax Planning Strategies</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0 text-xl" />
                <div>
                  <h3 className="font-bold mb-1">Tax Loss Harvesting</h3>
                  <p className="text-gray-600 text-sm">Strategic realization of losses to offset gains and reduce current year tax liability.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0 text-xl" />
                <div>
                  <h3 className="font-bold mb-1">Long-Term Capital Gains Optimization</h3>
                  <p className="text-gray-600 text-sm">Timing strategies to maximize beneficial long-term capital gains treatment.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0 text-xl" />
                <div>
                  <h3 className="font-bold mb-1">Qualified Opportunity Zones</h3>
                  <p className="text-gray-600 text-sm">Deployment of crypto gains into QOZ investments for tax deferral and potential exclusion.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0 text-xl" />
                <div>
                  <h3 className="font-bold mb-1">Charitable Giving Strategies</h3>
                  <p className="text-gray-600 text-sm">Tax-efficient cryptocurrency donations to maximize deductions and avoid recognition of gains.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0 text-xl" />
                <div>
                  <h3 className="font-bold mb-1">Retirement Account Strategies</h3>
                  <p className="text-gray-600 text-sm">Self-directed IRA and 401(k) strategies for tax-deferred or tax-free crypto investment growth.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <FaCheckCircle className="text-accent mt-1 flex-shrink-0 text-xl" />
                <div>
                  <h3 className="font-bold mb-1">Entity Restructuring</h3>
                  <p className="text-gray-600 text-sm">Optimal entity selection and restructuring to minimize overall tax burden for crypto businesses.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="section-title text-center mb-12">Why Choose Our Crypto Tax Services</h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="text-5xl mb-4">🎯</div>
                <h3 className="text-xl font-bold mb-3 text-primary">Crypto-Native Expertise</h3>
                <p className="text-gray-600">
                  Our CPAs specialize exclusively in cryptocurrency taxation and stay current with evolving regulations and guidance.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="text-5xl mb-4">🛡️</div>
                <h3 className="text-xl font-bold mb-3 text-primary">IRS Audit Protection</h3>
                <p className="text-gray-600">
                  Robust documentation and defensible positions to withstand IRS scrutiny and professional representation if needed.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="text-5xl mb-4">💡</div>
                <h3 className="text-xl font-bold mb-3 text-primary">Proactive Planning</h3>
                <p className="text-gray-600">
                  Year-round tax planning to minimize liabilities and maximize after-tax returns on your crypto investments.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Optimize Your Crypto Tax Position?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Schedule a consultation with our crypto tax specialists to discuss your tax planning and compliance needs.
            </p>
            <Link to="/contact" className="btn-primary bg-accent hover:bg-accent-dark">
              Schedule Tax Consultation
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}

export default CryptoTaxServices